/**
 * Haptic feedback for the Pokédex cockpit.
 *
 * Wraps the Vibration API with intent-named patterns so call sites read as
 * "what happened", not "how many milliseconds". Every call is a safe no-op
 * where vibration is unsupported or unwanted, so callers never need to guard.
 *
 * Support reality, stated plainly: `navigator.vibrate` works on Android
 * Chrome/Firefox. iOS Safari does NOT implement it, and desktop browsers have
 * no motor. So this is a progressive enhancement for touch devices — the
 * physical *feel* of the UI comes from the press-down states in cockpit.css,
 * which work everywhere.
 *
 * Used only by the two collectible card routes.
 */

/** Vibration is motion; honour the same accessibility preference. */
const reduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const supported = () =>
  typeof navigator !== "undefined" && typeof navigator.vibrate === "function";

/**
 * Patterns are deliberately short. Anything long enough to *notice* as a buzz
 * reads as a malfunction rather than a control; these should feel like the
 * click of a switch. Arrays alternate vibrate/pause in ms.
 */
const PATTERNS = {
  /** Passing a detent — dial ticks, slider steps. The lightest thing we do. */
  tick: 8,
  /** A control was pressed. */
  tap: 14,
  /** A selection committed — market chosen, tab switched. */
  select: [12, 28, 12],
  /** Toggle flipped on/off. */
  toggle: [10, 24, 18],
  /** Order submitted — the heaviest, and the only one meant to be felt through a pocket. */
  confirm: [18, 36, 26, 36, 40],
  /** Something was rejected or failed. */
  error: [40, 60, 40],
};

/**
 * Fire a haptic pattern.
 * @param {keyof PATTERNS} kind - Intent name, not a duration
 * @returns {boolean} True if a vibration was actually issued
 */
export function haptic(kind = "tap") {
  if (!supported() || reduced()) return false;
  const pattern = PATTERNS[kind];
  if (pattern == null) return false;
  try {
    return navigator.vibrate(pattern);
  } catch {
    // Some browsers throw when called outside a user gesture — never let
    // decoration break an interaction.
    return false;
  }
}

/** Convenience wrappers so call sites stay readable. */
export const hapticTick = () => haptic("tick");
export const hapticTap = () => haptic("tap");
export const hapticSelect = () => haptic("select");
export const hapticToggle = () => haptic("toggle");
export const hapticConfirm = () => haptic("confirm");
export const hapticError = () => haptic("error");

export default haptic;
